/**
 * StyledSelect Component
 * 
 * Reusable single-select dropdown with glassmorphism styling.
 * Features floating label on border (outlined/notched style).
 * Matches the style of MultiSelectDropdown for consistency.
 */
import { useState, useRef, useEffect, memo } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Option<T> {
    value: T
    label: string
    /** Optional description shown below the label in dropdown */
    description?: string
    icon?: React.ReactNode
}

interface StyledSelectProps<T extends string | number> {
    options: Option<T>[]
    value: T
    onChange: (value: T) => void
    placeholder?: string
    /** Floating label that appears on border */
    label?: string
    icon?: React.ReactNode
    disabled?: boolean
    className?: string
    /** Color accent for selected state */
    accentColor?: 'sky' | 'violet' | 'amber' | 'emerald' | 'rose' | 'slate'
}

function StyledSelectInner<T extends string | number>({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    label,
    icon,
    disabled = false,
    className,
    accentColor = 'sky',
}: StyledSelectProps<T>) {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Close on outside click
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleSelect = (optionValue: T) => {
        onChange(optionValue)
        setIsOpen(false)
    }

    // Find current option
    const currentOption = options.find(o => o.value === value)

    // Accent color classes
    const accentClasses = {
        sky: {
            border: 'border-sky-500/50 bg-sky-500/10',
            chevron: 'text-sky-500',
            selected: 'bg-sky-500/10 border-sky-500',
            text: 'text-sky-600 dark:text-sky-400',
            ring: 'focus:ring-sky-500/20',
        },
        violet: {
            border: 'border-violet-500/50 bg-violet-500/10',
            chevron: 'text-violet-500',
            selected: 'bg-violet-500/10 border-violet-500',
            text: 'text-violet-600 dark:text-violet-400',
            ring: 'focus:ring-violet-500/20',
        },
        amber: {
            border: 'border-amber-500/50 bg-amber-500/10',
            chevron: 'text-amber-500',
            selected: 'bg-amber-500/10 border-amber-500',
            text: 'text-amber-600 dark:text-amber-400',
            ring: 'focus:ring-amber-500/20',
        },
        emerald: {
            border: 'border-emerald-500/50 bg-emerald-500/10',
            chevron: 'text-emerald-500',
            selected: 'bg-emerald-500/10 border-emerald-500',
            text: 'text-emerald-600 dark:text-emerald-400',
            ring: 'focus:ring-emerald-500/20',
        },
        rose: {
            border: 'border-rose-500/50 bg-rose-500/10',
            chevron: 'text-rose-500',
            selected: 'bg-rose-500/10 border-rose-500',
            text: 'text-rose-600 dark:text-rose-400',
            ring: 'focus:ring-rose-500/20',
        },
        slate: {
            border: 'border-slate-500/50 bg-slate-500/10',
            chevron: 'text-slate-500',
            selected: 'bg-slate-500/10 border-slate-500',
            text: 'text-slate-600 dark:text-slate-400',
            ring: 'focus:ring-slate-500/20',
        },
    }

    const colors = accentClasses[accentColor]

    return (
        <div ref={dropdownRef} className={cn('relative', isOpen ? 'z-50' : 'z-10', className)}>
            {/* Trigger */}
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={cn(
                    'relative flex items-center gap-2 pl-3 pr-2.5',
                    // Add extra top padding when label present for floating label space
                    label ? 'pt-3 pb-2' : 'py-2',
                    'min-w-[140px] justify-between w-full',
                    'bg-[var(--glass-bg)] backdrop-blur-md',
                    'border border-[var(--glass-border)]',
                    'text-sm font-medium text-[var(--color-text-primary)]',
                    'hover:bg-[var(--color-surface-hover)]',
                    'focus:outline-none focus:ring-2',
                    colors.ring,
                    'transition-all duration-200 rounded-xl cursor-pointer',
                    value && colors.border,
                    disabled && 'opacity-50 cursor-not-allowed'
                )}
            >
                {/* Floating Label on Border */}
                {label && (
                    <span
                        className={cn(
                            'absolute left-2.5 -top-2 px-1',
                            'text-[11px] font-medium',
                            'bg-[var(--color-bg-primary)]',
                            'transition-colors duration-200',
                            value || isOpen
                                ? colors.chevron
                                : 'text-[var(--color-text-muted)]'
                        )}
                    >
                        {label}
                    </span>
                )}

                <div className="flex items-center gap-2 min-w-0">
                    {/* Show icon from option or prop */}
                    {(currentOption?.icon || icon) && (
                        <div className={cn(
                            "p-1 rounded-md transition-colors flex-shrink-0",
                            value ? "bg-[var(--color-surface-highlight)]" : "bg-[var(--color-surface-hover)]"
                        )}>
                            {currentOption?.icon || icon}
                        </div>
                    )}
                    <span className="truncate">
                        {currentOption?.label || placeholder}
                    </span>
                </div>

                <ChevronDown className={cn(
                    'w-4 h-4 text-[var(--color-text-muted)] flex-shrink-0',
                    'transition-transform duration-200',
                    isOpen && `rotate-180 ${colors.chevron}`
                )} />
            </button>

            {/* Dropdown Panel */}
            {isOpen && (
                <div className={cn(
                    'absolute top-full left-0 mt-2 w-full min-w-[320px] overflow-hidden',
                    'bg-[var(--color-surface)] border border-[var(--color-border)]',
                    'rounded-xl shadow-xl z-50',
                    'animate-in fade-in slide-in-from-top-2 duration-200'
                )}>
                    {/* Options List */}
                    <ul className="max-h-80 overflow-y-auto py-1">
                        {options.map((option) => {
                            const isSelected = option.value === value
                            const hasDescription = !!option.description
                            return (
                                <li key={String(option.value)}>
                                    <button
                                        type="button"
                                        onClick={() => handleSelect(option.value)}
                                        className={cn(
                                            'w-full flex items-start gap-2.5 px-4 text-left',
                                            hasDescription ? 'py-3' : 'py-2.5',
                                            'transition-all duration-150 border-l-2',
                                            isSelected
                                                ? colors.selected
                                                : 'border-transparent hover:bg-[var(--color-surface-hover)]'
                                        )}
                                    >
                                        {option.icon && (
                                            <span className="flex-shrink-0 mt-0.5">{option.icon}</span>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <span className={cn(
                                                'block font-medium',
                                                hasDescription ? 'text-sm' : 'text-sm',
                                                isSelected ? colors.text : 'text-[var(--color-text-primary)]'
                                            )}>
                                                {option.label}
                                            </span>
                                            {hasDescription && (
                                                <span className={cn(
                                                    'block text-xs leading-relaxed mt-0.5',
                                                    isSelected
                                                        ? 'text-[var(--color-text-secondary)]'
                                                        : 'text-[var(--color-text-muted)]'
                                                )}>
                                                    {option.description}
                                                </span>
                                            )}
                                        </div>
                                        {isSelected && (
                                            <Check className={cn('w-4 h-4 flex-shrink-0 mt-0.5', colors.chevron)} />
                                        )}
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            )}
        </div>
    )
}

export const StyledSelect = memo(StyledSelectInner) as typeof StyledSelectInner
